import { Link, useLocation } from "wouter";
import { useState, useEffect, useCallback, useMemo } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { ColorLegendFooter } from "@/components/ColorLegendFooter";
import { BandPill, BandBar } from "@/components/Band";
import { CountdownToClose } from "@/components/CountdownToClose";
import { api } from "@/lib/session";
import { friendlyError } from "@/lib/friendly-error";
import { type ColorBand } from "@/lib/scoring";
import { ArrowUpRight, RefreshCw, ChevronUp, ChevronDown, Search, X, Download } from "lucide-react";
import { exportRows } from "@/lib/csvExport";
import HelixCompositeBadge from "@/components/helix/HelixCompositeBadge";
import SprintBoyToggle from "@/components/SprintBoyToggle";
import { DnaGlyph } from "@/components/DnaGlyph";
import { useSprintView } from "@/lib/sprint-context";
import { type SourceOrigin } from "@shared/source-origin";
// W10-DemoDataScrub: client-side guard for demo/smoke-test accounts
import { isDemoData } from "@shared/demoDataFilter";

// Shape returned by GET /api/accounts
export interface ApiAccount {
  id: number;
  name: string;
  industry: string;
  stage: string;
  ownerId: number;
  estArr: number;
  score: number;
  bucket: string | null;
  updatedAt: number;
  targetCloseDate?: string | null;
  is_recommended?: boolean;
  sprint_target?: boolean;
  is_archived?: boolean;
  // source_origin — set by AE / BDR. Null until populated.
  sourceOrigin?: SourceOrigin | null;
}

// Shape returned by GET /api/accounts/:id/summary
interface AccountSummary {
  account: ApiAccount;
  bucket: "OMITTED" | "UPSIDE" | "PROBABLE" | "COMMIT" | null;
  score: number;
  greenCount: number;
  scoredCount: number;
  redHardKill: boolean;
}

// Shape returned by GET /api/manager/portfolio (superset of ApiAccount)
interface PortfolioAccount extends ApiAccount {
  ownerName: string;
  ownerEmail: string;
  decisionMakerCount: number;
  actionCount: number;
  greenCount: number;
  alerts: string[];
}

/** Map server bucket string to a ColorBand for BandPill/BandBar */
export function bucketToColorBand(bucket: string | null | undefined): ColorBand {
  switch (bucket) {
    case "COMMIT":   return "green";
    case "PROBABLE": return "green";
    case "UPSIDE":   return "amber";
    case "OMITTED":  return "red";
    default:         return "tbd";
  }
}

/**
 * SourcePill — "AIP" by default, "CRM" once accounts.crm_source is populated.
 */
function SourcePill({ account }: { account: ApiAccount }) {
  const ext = account as ApiAccount & { crm_source?: string | null };
  const fromCrm = !!ext.crm_source;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded font-semibold uppercase"
      style={{
        background: fromCrm ? "#F5F5F4" : "#EFEFEE",
        color: fromCrm ? "#0B0B0F" : "#525258",
        fontSize: 10,
        padding: "2px 8px",
        letterSpacing: "0.08em",
        border: "1px solid #E5E7EB",
      }}
    >
      {fromCrm ? "CRM" : "Attilius"}
    </span>
  );
}

export interface ApiQuestion {
  key: string;
  answer: { selectedOption: string } | null;
}
export function questionsToAnswerMap(questions: ApiQuestion[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const q of questions) {
    if (q.answer?.selectedOption) {
      out[q.key] = q.answer.selectedOption;
    }
  }
  return out;
}

interface AccountRow {
  account: ApiAccount;
  score: number;
  bucket: string | null;
  greenCount: number;
  scoredCount: number;
}

// ─── Sort state ───────────────────────────────────────────────────────────────

type SortKey = "name" | "stage" | "estArr" | "score" | "bucket" | "daysInStage" | "lastActivity" | "owner" | "source";
type SortDir = "asc" | "desc";
// D-054: Tier filter — computed from sorted M-PACT rank after fetch
type TierFilter = "all" | "tier1" | "tier2" | "tier3";

const SORT_OPTIONS: { key: SortKey; label: string; defaultDir: SortDir }[] = [
  { key: "score",        label: "Composite score",   defaultDir: "desc" },
  { key: "name",         label: "Name",            defaultDir: "asc"  },
  { key: "owner",        label: "Owner",           defaultDir: "asc"  },
  { key: "source",       label: "Source",          defaultDir: "asc"  },
  { key: "stage",        label: "Stage",           defaultDir: "asc"  },
  { key: "daysInStage",  label: "Days in stage",   defaultDir: "desc" },
  { key: "lastActivity", label: "Last activity",   defaultDir: "desc" },
];

function SortIcon({ col, sortKey, sortDir }: { col: SortKey; sortKey: SortKey; sortDir: SortDir }) {
  if (col !== sortKey) {
    return (
      <span className="inline-flex flex-col opacity-30 ml-1">
        <ChevronUp className="w-2.5 h-2.5 -mb-1" />
        <ChevronDown className="w-2.5 h-2.5" />
      </span>
    );
  }
  return sortDir === "asc"
    ? <ChevronUp className="w-3 h-3 ml-1 text-[#0B0B0F]" />
    : <ChevronDown className="w-3 h-3 ml-1 text-[#0B0B0F]" />;
}

export default function AccountList() {
  const [rows, setRows] = useState<AccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, navigate] = useLocation();

  // Search
  const [search, setSearch] = useState("");
  const { view } = useSprintView();
  // Default ON. If the rep has previously toggled it off (stored as "0" in
  // localStorage), honor that choice; otherwise fall back to "all active" (no sprint
  // constraint). W36-B: default changed from true → false so accounts 208/209 and
  // other non-sprint-target accounts appear in the default view. Sprint filter is
  // still available as an opt-in toggle.
  const [sprintOnly, setSprintOnly] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem("aip:accounts:sprintOnly");
      if (stored === "0") return false;
      if (stored === "1") return true;
    } catch {
      // ignore
    }
    // Default: show all active accounts (not just sprint targets)
    return false;
  });

  useEffect(() => {
    try {
      localStorage.setItem("aip:accounts:sprintOnly", sprintOnly ? "1" : "0");
    } catch {
      // ignore
    }
  }, [sprintOnly]);

  // Sort
  const [sortKey, setSortKey] = useState<SortKey>("score");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  // D-054: Tier filter
  const [tierFilter, setTierFilter] = useState<TierFilter>("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let rowData: AccountRow[];
      try {
        const portfolio = await api<PortfolioAccount[]>("/api/manager/portfolio");
        rowData = portfolio.map((a) => ({
          account: a,
          score: a.score ?? 0,
          bucket: a.bucket ?? null,
          greenCount: a.greenCount ?? 0,
          scoredCount: 9,
        }));
      } catch (portfolioErr: any) {
        if (portfolioErr?.status === 403) {
          const accounts = await api<ApiAccount[]>("/api/accounts");
          rowData = await Promise.all(
            accounts.map(async (a) => {
              try {
                const s = await api<AccountSummary>(`/api/accounts/${a.id}/summary`);
                return {
                  account: a,
                  score: s.score ?? 0,
                  bucket: s.bucket ?? null,
                  greenCount: s.greenCount ?? 0,
                  scoredCount: s.scoredCount ?? 9,
                };
              } catch {
                return {
                  account: a,
                  score: a.score ?? 0,
                  bucket: a.bucket ?? null,
                  greenCount: 0,
                  scoredCount: 0,
                };
              }
            })
          );
        } else {
          throw portfolioErr;
        }
      }
      // W10-DemoDataScrub: client-side safety net — strip any demo rows that
      // slipped through the server filter (e.g. cached responses).
      setRows(rowData.filter((r) => !isDemoData((r.account as any)?.name ?? (r as any).name)));
    } catch (e: any) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Sort handler
  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      const opt = SORT_OPTIONS.find((o) => o.key === key);
      setSortDir(opt?.defaultDir ?? "desc");
    }
  }

  // Filtered + sorted rows
  const displayRows = useMemo(() => {
    let filtered = rows.filter(r => !r.account.is_archived);
    const q = search.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter(
        (r) => {
          const ownerName = ((r.account as any).ownerName || "").toString().toLowerCase();
          const ownerEmail = ((r.account as any).ownerEmail || "").toString().toLowerCase();
          return (
            r.account.name.toLowerCase().includes(q) ||
            r.account.industry.toLowerCase().includes(q) ||
            r.account.stage.toLowerCase().includes(q) ||
            ownerName.includes(q) ||
            ownerEmail.includes(q)
          );
        }
      );
    }
    if (sprintOnly) {
      const sprintFiltered = filtered.filter((r) => (r.account as any).sprint_target === true);
      // If sprint filter would hide every account (no accounts flagged as sprint targets for this tenant), fall back to showing all so the list is not silently empty.
      if (sprintFiltered.length > 0) filtered = sprintFiltered;
    }

    // D-054: Tier filter — rank by M-PACT score (desc) then assign tiers
    if (tierFilter !== "all") {
      const ranked = [...filtered].sort((a, b) => b.score - a.score);
      const tier1 = new Set(ranked.slice(0, 10).map((r) => r.account.id));
      const tier2 = new Set(ranked.slice(10, 30).map((r) => r.account.id));
      if (tierFilter === "tier1") {
        filtered = filtered.filter((r) => tier1.has(r.account.id));
      } else if (tierFilter === "tier2") {
        filtered = filtered.filter((r) => tier2.has(r.account.id));
      } else if (tierFilter === "tier3") {
        filtered = filtered.filter((r) => !tier1.has(r.account.id) && !tier2.has(r.account.id));
      }
    }

    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "name":
          cmp = a.account.name.localeCompare(b.account.name);
          break;
        case "owner": {
          const ownerA = ((a.account as any).ownerName || (a.account as any).ownerEmail || "").toString().toLowerCase();
          const ownerB = ((b.account as any).ownerName || (b.account as any).ownerEmail || "").toString().toLowerCase();
          cmp = ownerA.localeCompare(ownerB);
          break;
        }
        case "source": {
          // Nulls sort last in both directions so "unset" doesn't dominate the top.
          const srcA = a.account.sourceOrigin ?? "";
          const srcB = b.account.sourceOrigin ?? "";
          if (srcA === "" && srcB === "") cmp = 0;
          else if (srcA === "") cmp = 1;
          else if (srcB === "") cmp = -1;
          else cmp = srcA.localeCompare(srcB);
          break;
        }
        case "stage":
          cmp = a.account.stage.localeCompare(b.account.stage);
          break;
        case "estArr":
          cmp = (a.account.estArr ?? 0) - (b.account.estArr ?? 0);
          break;
        case "score":
          cmp = a.score - b.score;
          break;
        case "bucket": {
          const order = ["COMMIT", "PROBABLE", "UPSIDE", "OMITTED", null];
          cmp = order.indexOf(a.bucket) - order.indexOf(b.bucket);
          break;
        }
        case "daysInStage": {
          // updatedAt is ms epoch; days-in-stage approximated as time since last update
          const now = Date.now();
          const daysA = (now - (a.account.updatedAt ?? 0)) / 86400000;
          const daysB = (now - (b.account.updatedAt ?? 0)) / 86400000;
          cmp = daysA - daysB;
          break;
        }
        case "lastActivity": {
          cmp = (a.account.updatedAt ?? 0) - (b.account.updatedAt ?? 0);
          break;
        }
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [rows, search, sortKey, sortDir, sprintOnly, tierFilter]);

  return (
    <OperatorShell
      breadcrumbs={[{ label: "Home", href: "/" }, { label: "Accounts" }]}
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">
            My accounts
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Pipeline</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Open an account to see the qualification grid.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 self-start sm:self-auto">
          <button
            data-tour="add-expansion-btn"
            className="text-xs text-[#0B0B0F] inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded border border-[#0B0B0F] bg-background hover:bg-[#0B0B0F] hover:text-white whitespace-nowrap transition-colors"
            onClick={() => { window.location.hash = "/accounts/new"; }}
            title="Add expansion opportunity to an existing customer"
          >
            + Add expansion
          </button>
          <button
            className="text-xs text-[#0B0B0F] inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded border border-[#0B0B0F] bg-background hover:bg-[#0B0B0F] hover:text-white whitespace-nowrap transition-colors"
            onClick={() => { window.location.hash = "/upload-contacts"; }}
            title="Bulk upload curated contacts that already pass at least one qualifier letter"
          >
            Upload contacts
          </button>
          <button
            className="text-xs text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))] inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded border border-border whitespace-nowrap transition-colors"
            onClick={() => {
              const exportRowsData = (rows || []).map(r => ({
                id: r.id,
                name: r.name,
                industry: r.industry,
                status: r.status,
                product: r.product,
                deal_verdict: r.deal_verdict,
                score: (r as any).score,
                close_date: (r as any).close_date,
                arr_estimate: (r as any).arr_estimate,
                owner_name: (r as any).owner_name,
                stage: (r as any).stage,
              }));
              exportRows("accounts", exportRowsData);
            }}
            title="Download visible accounts as CSV"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button
            className="text-xs text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))] inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded border border-border whitespace-nowrap transition-colors"
            onClick={load}
            title="Refresh accounts"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-3">
        <SprintBoyToggle />
        <label className="inline-flex items-center gap-2 text-xs text-neutral-700">
          <input
            type="checkbox"
            checked={sprintOnly}
            onChange={(e) => setSprintOnly(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 text-[#DC2626] focus:ring-[#DC2626]"
            data-testid="sprint-target-filter"
          />
          Jumpstart targets only
        </label>
        {/* D-054: Sort dropdown */}
        <div className="inline-flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Sort</span>
          <select
            value={sortKey}
            onChange={(e) => {
              const key = e.target.value as SortKey;
              handleSort(key);
            }}
            className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-[#0B0B0F]"
            data-testid="sort-select"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
          <button
            onClick={() => setSortDir((d) => d === "asc" ? "desc" : "asc")}
            className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground hover:bg-[hsl(var(--surface-2))] transition-colors"
            title={sortDir === "asc" ? "Ascending" : "Descending"}
          >
            {sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>
        {/* D-054: Tier filter */}
        <div className="inline-flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Tier</span>
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value as TierFilter)}
            className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-[#0B0B0F]"
            data-testid="tier-filter"
          >
            <option value="all">All</option>
            <option value="tier1">Tier 1 (top 10)</option>
            <option value="tier2">Tier 2 (next 20)</option>
            <option value="tier3">Tier 3 (rest)</option>
          </select>
        </div>
      </div>

      {/* ── Search bar ── */}
      <div className="mb-3 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          placeholder="Search accounts by name, industry, stage, or owner..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-9 py-2.5 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F] placeholder:text-muted-foreground"
        />
        {search && (
          <button
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            onClick={() => setSearch("")}
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {loading && (
        <div className="card-shell p-8 text-sm text-muted-foreground">Loading accounts...</div>
      )}

      {error && (
        <div className="card-shell p-8 text-sm text-rose-400">{error}</div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="card-shell p-8 text-sm text-muted-foreground">No accounts found.</div>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          {/* Search result count */}
          {search.trim() && (
            <div className="text-[11px] text-muted-foreground mb-3">
              {displayRows.length} of {rows.length} accounts
            </div>
          )}

          {/* Desktop / tablet table layout */}
          <div className="card-shell overflow-hidden hidden sm:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                  {/* Sortable: Account */}
                  <th
                    className="text-left px-3 py-2.5 font-medium cursor-pointer hover:text-foreground select-none"
                    onClick={() => handleSort("name")}
                    title="Sort by account name"
                  >
                    <span className="inline-flex items-center">
                      Account
                      <SortIcon col="name" sortKey={sortKey} sortDir={sortDir} />
                    </span>
                  </th>
                  <th
                    className="text-left px-3 py-2.5 font-medium"
                    title="Where this account was created. CRM-sourced accounts sync inbound."
                  >
                    From
                  </th>
                  {/* Sortable: Owner / AE */}
                  <th
                    className="text-left px-3 py-2.5 font-medium cursor-pointer hover:text-foreground select-none"
                    onClick={() => handleSort("owner")}
                    title="Sort by owner / account executive"
                  >
                    <span className="inline-flex items-center">
                      Owner
                      <SortIcon col="owner" sortKey={sortKey} sortDir={sortDir} />
                    </span>
                  </th>
                  {/* Sortable: Source (source_origin — how the account came in) */}
                  <th
                    className="text-left px-3 py-2.5 font-medium cursor-pointer hover:text-foreground select-none"
                    onClick={() => handleSort("source")}
                    title="Sort by source. Open the account to set or change."
                  >
                    <span className="inline-flex items-center">
                      Source
                      <SortIcon col="source" sortKey={sortKey} sortDir={sortDir} />
                    </span>
                  </th>
                  {/* Sortable: Stage */}
                  <th
                    className="text-left px-3 py-2.5 font-medium cursor-pointer hover:text-foreground select-none"
                    onClick={() => handleSort("stage")}
                    title="Sort by stage"
                  >
                    <span className="inline-flex items-center">
                      Stage
                      <SortIcon col="stage" sortKey={sortKey} sortDir={sortDir} />
                    </span>
                  </th>
                  {/* D-010: EST ARR column hidden — data not yet populated */}
                  <th className="text-left px-3 py-2.5 font-medium">Close</th>
                  {/* Sortable: Composite */}
                  <th
                    className="text-left px-3 py-2.5 font-medium w-[200px] cursor-pointer hover:text-foreground select-none"
                    onClick={() => handleSort("score")}
                    title="Sort by composite score"
                  >
                    <span className="inline-flex items-center">
                      Composite
                      <SortIcon col="score" sortKey={sortKey} sortDir={sortDir} />
                    </span>
                  </th>
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {displayRows.map(({ account, score, bucket, greenCount }) => {
                  const band = score > 0 ? bucketToColorBand(bucket) : "tbd";
                  return (
                    <tr
                      key={account.id}
                      onClick={() => navigate(`/account/${account.id}`)}
                      className="border-b border-border last:border-0 hover:bg-[hsl(var(--surface-2))] cursor-pointer transition-colors leading-tight group"
                    >
                      <td className="px-3 py-2.5">
                        <div className="min-w-0">
                          <div className="font-medium text-foreground">{account.name}</div>
                          <div className="text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">{account.industry}</div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <SourcePill account={account} />
                      </td>
                      <td className="px-3 py-2.5 text-foreground">
                        {(() => {
                          const ownerName = (account as any).ownerName as string | undefined;
                          const ownerEmail = (account as any).ownerEmail as string | undefined;
                          if (ownerName && ownerName.trim()) return ownerName;
                          if (ownerEmail && ownerEmail.trim()) return ownerEmail.split("@")[0];
                          return <span className="text-muted-foreground">—</span>;
                        })()}
                      </td>
                      <td className="px-3 py-2.5">
                        {account.sourceOrigin ? (
                          <span className="text-sm text-foreground">{account.sourceOrigin}</span>
                        ) : (
                          <Link
                            href={`/account/${account.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground"
                            title="Open the account to set how it came in"
                            aria-label={`Set source for ${account.name}`}
                            data-testid={`set-source-link-${account.id}`}
                          >
                            Set source <ChevronUp className="w-3 h-3 rotate-90" />
                          </Link>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">{account.stage}</td>
                      {/* D-010: EST ARR column hidden — data not yet populated */}
                      <td className="px-3 py-2.5">
                        {/* D-006: hide close date for Suspect stage — no close date expected */}
                        {account.stage !== "Suspect" && (
                          <CountdownToClose
                            targetCloseDate={account.targetCloseDate}
                            lastActivity={account.updatedAt}
                            compact
                          />
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        {/* D-053: pass preloadedComposite so the badge shows the server-computed
                            score (from account_answers) instead of showing 0 while localStorage
                            is unhydrated. */}
                        <div className="flex items-center gap-2">
                          <HelixCompositeBadge
                            accountId={String(account.id)}
                            size="sm"
                            preloadedComposite={score}
                            onClick={(e?: any) => { if (e) e.stopPropagation(); }}
                          />
                          {score > 0 ? (
                            <span className="tabular text-sm font-semibold text-foreground">
                              {score.toFixed(1)}
                            </span>
                          ) : (
                            <span
                              className="tabular text-sm font-semibold"
                              style={{ color: "#6B7280" }}
                              title="Score not yet computed"
                            >
                              Computing
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Link
                          href={`/account/${account.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[40px] rounded border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))] hover:border-[hsl(var(--accent-red))]/40 transition-colors"
                          aria-label={`Open ${account.name} scorecard`}
                        >
                          Open <ArrowUpRight className="w-3.5 h-3.5" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
                {displayRows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-sm text-muted-foreground text-center">
                      {search ? `No accounts match "${search}"` : "No accounts to show yet."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile card layout */}
          <div className="sm:hidden space-y-3">
            {displayRows.map(({ account, score, bucket, greenCount }) => (
              <AccountMobileCard
                key={account.id}
                account={account}
                score={score}
                bucket={bucket}
                greenCount={greenCount}
              />
            ))}
            {displayRows.length === 0 && (
              <div className="card-shell p-6 text-sm text-muted-foreground text-center">
                {search ? `No accounts match "${search}"` : "No accounts to show yet."}
              </div>
            )}
          </div>
        </>
      )}
      {/* T2 Item 4 — Color Legend Footer (Canon §19) */}
      <div style={{ padding: "0 16px 16px" }}><ColorLegendFooter /></div>
    </OperatorShell>
  );
}

function AccountMobileCard({
  account,
  score,
  bucket,
  greenCount,
}: {
  account: ApiAccount;
  score: number;
  bucket: string | null;
  greenCount: number;
}) {
  const band = score > 0 ? bucketToColorBand(bucket) : "tbd";
  return (
    <Link
      href={`/account/${account.id}`}
      className="card-shell p-3 block active:bg-[hsl(var(--surface-2))] hover:bg-[hsl(var(--surface-2))] transition-colors min-h-[44px]"
      aria-label={`Open ${account.name} scorecard`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <a
            href={`/account/${account.id}`}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Open DNA for ${account.name}`}
            className="shrink-0 inline-flex items-center justify-center rounded-md p-0.5 text-foreground/70"
          >
            <DnaGlyph score={score} size={20} ariaLabel={`${account.name} DNA`} />
          </a>
          <div className="min-w-0">
            <div className="font-medium text-foreground text-[15px] leading-tight">
              {account.name}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">{account.industry}</div>
          </div>
        </div>
        <ArrowUpRight className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Stage</div>
          <div className="text-foreground mt-0.5">{account.stage}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Est ARR</div>
          <div className="text-foreground tabular mt-0.5">
            {account.estArr > 0 ? `$${(account.estArr / 1000).toFixed(0)}k` : "—"}
          </div>
        </div>
        {/* D-006: hide close date for Suspect stage */}
        {account.stage !== "Suspect" && (
          <div className="col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Close date</div>
            <div className="mt-0.5">
              <CountdownToClose
                targetCloseDate={account.targetCloseDate}
                lastActivity={account.updatedAt}
                compact
              />
            </div>
          </div>
        )}
        <div className="col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Composite
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <HelixCompositeBadge accountId={String(account.id)} size="sm" preloadedComposite={score} onClick={() => {}} />
            {score > 0 ? (
              <span className="tabular font-semibold text-foreground">
                {score.toFixed(1)}
              </span>
            ) : (
              <span
                className="tabular font-semibold"
                style={{ color: "#6B7280" }}
                title="Score not yet computed"
              >
                Computing
              </span>
            )}
            <BandPill band={band} />
            {greenCount > 0 && (
              <span className="text-[11px] text-muted-foreground tabular">
                {greenCount}/9 green
              </span>
            )}
          </div>
        </div>
      </div>

      <div title="Composite score band">
        <BandBar band={band} value={score} />
      </div>
    </Link>
  );
}
